import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight, BellRing, CalendarClock, ListChecks, UserRound, X } from 'lucide-react';
import { Button } from '../ui/button';
import { formatDate, QUALITY_POPUP_KEY, timeLeftLabel } from './qualityConfig';

const readDismissed = () => {
    try { return JSON.parse(localStorage.getItem(QUALITY_POPUP_KEY) || '[]'); } catch { return []; }
};

const PopupTask = ({ task }) => (
    <li className="rounded-xl border bg-background/70 px-3 py-2.5" data-testid={`quality-popup-task-${task.id}`}>
        {task.nudged && (
            <p className="mb-1 inline-flex items-center gap-1 rounded-full bg-amber-400/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-300" data-testid={`quality-popup-reminder-${task.id}`}>
                <BellRing className="h-3 w-3" /> Reminder from {task.nudged_by_name}
            </p>
        )}
        <p className="truncate text-sm font-semibold">{task.title}</p>
        <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1"><UserRound className="h-3 w-3" />{task.assigned_by_name}</span>
            <span className={`inline-flex items-center gap-1 ${task.due_soon ? 'font-semibold text-amber-600 dark:text-amber-400' : ''}`}>
                <CalendarClock className="h-3 w-3" />Due {formatDate(task.deadline)} · {timeLeftLabel(task.hours_left)}
            </span>
            <span className="inline-flex items-center gap-1"><ListChecks className="h-3 w-3" />{task.total_count} simulation{task.total_count > 1 ? 's' : ''}</span>
        </p>
    </li>
);

const popupTitle = (summary) => {
    if (summary.nudged === summary.new) return summary.new > 1 ? `${summary.new} reminders from your Quality reviewer` : 'Reminder from your Quality reviewer';
    if (summary.nudged > 0) return `${summary.new} Quality Tasks need your attention`;
    return summary.new > 1 ? `${summary.new} new Quality Tasks` : 'New Quality Task assigned';
};

export const NewTaskPopup = ({ summary, blocked }) => {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [dismissed, setDismissed] = useState(readDismissed);
    const [ready, setReady] = useState(false);
    useEffect(() => { const t = setTimeout(() => setReady(true), 800); return () => clearTimeout(t); }, []);

    const tasks = summary?.new_tasks || [];
    const ids = tasks.map((t) => t.token);
    const visible = ready && !blocked && !['/quality-tasks', '/quality-admin'].includes(pathname) && ids.some((id) => !dismissed.includes(id));
    const more = (summary?.new || 0) - tasks.length;

    const close = () => {
        const next = [...new Set([...dismissed, ...ids])];
        localStorage.setItem(QUALITY_POPUP_KEY, JSON.stringify(next));
        setDismissed(next);
    };
    const open = () => { close(); navigate('/quality-tasks'); };

    return (
        <AnimatePresence>
            {visible && (
                <motion.div
                    role="dialog"
                    aria-label="New Quality Task assigned"
                    initial={{ opacity: 0, y: 24, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 16, scale: 0.98 }}
                    transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                    className="pointer-events-auto w-full overflow-hidden rounded-2xl border border-violet-500/30 bg-card/95 shadow-2xl backdrop-blur-md"
                    data-testid="quality-new-task-popup"
                >
                    <div className="h-1 bg-red-500" />
                    <div className="space-y-4 p-5">
                        <div className="flex items-start gap-3">
                            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-red-500/10 text-red-600 dark:text-red-400">
                                <BellRing className="h-5 w-5 origin-top animate-[wiggle_1s_ease-in-out_3]" />
                            </span>
                            <div className="min-w-0 flex-1">
                                <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-red-600 dark:text-red-400">Action required</p>
                                <h3 className="font-heading text-lg font-bold leading-tight" data-testid="quality-popup-title">
                                    {popupTitle(summary)}
                                </h3>
                            </div>
                            <button type="button" onClick={close} aria-label="Close" data-testid="close-quality-tasks-popup-btn"
                                className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                        <p className="text-sm text-muted-foreground">Your Quality reviewer picked simulations for you. Your best score before the deadline counts.</p>
                        <ul className="space-y-2">{tasks.map((t) => <PopupTask key={t.id} task={t} />)}</ul>
                        {more > 0 && <p className="text-xs text-muted-foreground" data-testid="quality-popup-more">+{more} more waiting for you</p>}
                        <Button onClick={open} className="w-full bg-violet-600 text-white hover:bg-violet-700" data-testid="open-quality-tasks-popup-btn">
                            Open Quality Tasks <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
